(() => {
  'use strict';

  if (window.tuckCartController) return;

  const SELECTORS = {
    root: '#TuckCartDrawer',
    render: '[data-tuck-cart-render]',
    panel: '.tuck-cart-drawer__panel',
    error: '[data-tuck-cart-error]',
    focusable: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const SWIPE = {
    intentDistance: 8,
    directionRatio: 1.2,
    closeDistance: 96,
    flickDistance: 48,
    flickVelocity: 0.55
  };

  class TuckCartController {
    constructor() {
      this.root = null;
      this.opener = null;
      this.requestController = null;
      this.recommendationController = null;
      this.recommendationCache = new Map();
      this.sequence = 0;
      this.viewTracked = false;
      this.opening = false;
      this.openAnimation = null;
      this.closeAnimationCleanup = null;
      this.swipeGesture = null;
      this.touchRoot = null;
      this.historyEntryActive = false;
      this.boundClick = this.onClick.bind(this);
      this.boundChange = this.onChange.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundPopstate = this.onPopstate.bind(this);
      this.boundPageShow = this.onPageShow.bind(this);
      this.boundAjaxAdded = this.onAjaxAdded.bind(this);
      this.boundProductSuccess = this.onProductSuccess.bind(this);
      this.boundProductNavigate = this.onProductNavigate.bind(this);
      this.boundSuppressRebuy = this.suppressRebuyCart.bind(this);
      this.boundTouchStart = this.onTouchStart.bind(this);
      this.boundTouchMove = this.onTouchMove.bind(this);
      this.boundTouchEnd = this.onTouchEnd.bind(this);
      this.init();
    }

    init() {
      this.syncRoot();
      // Capture the cart trigger before the legacy theme drawer can swallow it.
      document.addEventListener('click', this.boundClick, true);
      document.addEventListener('change', this.boundChange);
      document.addEventListener('keydown', this.boundKeydown);
      window.addEventListener('popstate', this.boundPopstate);
      window.addEventListener('pageshow', this.boundPageShow);
      document.addEventListener('ajaxProduct:added', this.boundAjaxAdded);
      document.addEventListener('product-form:submit:success', this.boundProductSuccess);
      document.addEventListener('product-form:cart:navigate', this.boundProductNavigate);
      document.addEventListener('rebuy:smartcart.ready', this.boundSuppressRebuy);
      document.addEventListener('rebuy:cart.ready', this.boundSuppressRebuy);
      document.addEventListener('rebuy:cart.change', this.boundSuppressRebuy);
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('shopify:section:load', () => {
        this.syncRoot();
        this.loadFallbackRecommendations();
        this.rankRecommendationsByCartSize();
      });
      document.documentElement.classList.add('tuck-native-cart-enabled');
      this.syncScrollLock();
      this.suppressRebuyCart();
      this.loadFallbackRecommendations();
      this.rankRecommendationsByCartSize();
    }

    syncRoot() {
      const nextRoot = document.querySelector(SELECTORS.root);
      if (nextRoot !== this.touchRoot) {
        this.touchRoot?.removeEventListener('touchstart', this.boundTouchStart);
        this.touchRoot?.removeEventListener('touchmove', this.boundTouchMove);
        this.touchRoot?.removeEventListener('touchend', this.boundTouchEnd);
        this.touchRoot?.removeEventListener('touchcancel', this.boundTouchEnd);
        nextRoot?.addEventListener('touchstart', this.boundTouchStart, { passive: true });
        nextRoot?.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        nextRoot?.addEventListener('touchend', this.boundTouchEnd, { passive: true });
        nextRoot?.addEventListener('touchcancel', this.boundTouchEnd, { passive: true });
        this.touchRoot = nextRoot;
      }
      this.root = nextRoot;
      return this.root;
    }

    onTouchStart(event) {
      if (
        !this.root ||
        this.root.getAttribute('aria-hidden') !== 'false' ||
        event.touches.length !== 1
      ) return;

      const panel = event.target.closest(SELECTORS.panel);
      if (!panel || !this.root.contains(panel)) return;
      if (event.target.closest('.tuck-cart-recommendations__track')) return;
      if (event.target.closest('input, select, textarea, label, [contenteditable="true"]')) return;

      const touch = event.touches[0];
      this.swipeGesture = {
        identifier: touch.identifier,
        panel,
        overlay: this.root.querySelector('.tuck-cart-drawer__overlay'),
        startX: touch.clientX,
        startY: touch.clientY,
        startedAt: performance.now(),
        distance: 0,
        dragging: false,
        cancelled: false
      };
    }

    onTouchMove(event) {
      const gesture = this.swipeGesture;
      if (!gesture || gesture.cancelled) return;

      const touch = Array.from(event.touches).find((candidate) => candidate.identifier === gesture.identifier);
      if (!touch) return;

      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);

      if (!gesture.dragging) {
        if (Math.max(horizontalDistance, verticalDistance) < SWIPE.intentDistance) return;
        if (deltaX <= 0 || horizontalDistance < verticalDistance * SWIPE.directionRatio) {
          gesture.cancelled = true;
          return;
        }
        gesture.dragging = true;
        this.root.classList.add('tuck-cart-drawer--dragging');
      }

      event.preventDefault();
      const panelWidth = gesture.panel.getBoundingClientRect().width;
      gesture.distance = Math.min(Math.max(deltaX, 0), panelWidth);
      gesture.panel.style.transform = `translate3d(${gesture.distance}px, 0, 0)`;
      if (gesture.overlay) {
        gesture.overlay.style.opacity = String(Math.max(0, 1 - (gesture.distance / panelWidth)));
      }
    }

    onTouchEnd() {
      const gesture = this.swipeGesture;
      this.swipeGesture = null;
      if (!gesture || !gesture.dragging) return;

      const elapsed = Math.max(performance.now() - gesture.startedAt, 1);
      const velocity = gesture.distance / elapsed;
      const shouldClose = gesture.distance >= SWIPE.closeDistance ||
        (gesture.distance >= SWIPE.flickDistance && velocity >= SWIPE.flickVelocity);

      this.root.classList.remove('tuck-cart-drawer--dragging');
      gesture.panel.getBoundingClientRect();

      if (shouldClose) {
        this.close({ preserveSwipeTransform: true });
      }

      gesture.panel.style.removeProperty('transform');
      gesture.overlay?.style.removeProperty('opacity');
    }

    resetSwipeGesture() {
      const gesture = this.swipeGesture;
      this.swipeGesture = null;
      this.root?.classList.remove('tuck-cart-drawer--dragging');
      gesture?.panel.style.removeProperty('transform');
      gesture?.overlay?.style.removeProperty('opacity');
    }

    cancelCloseAnimation() {
      if (this.closeAnimationCleanup) {
        this.closeAnimationCleanup();
        return;
      }
      this.root?.classList.remove('tuck-cart-drawer--closing');
    }

    startCloseAnimation(panel) {
      const closingRoot = this.root;
      closingRoot.classList.add('tuck-cart-drawer--closing');

      if (this.reducedMotion() || Number.parseFloat(getComputedStyle(panel).transitionDuration) === 0) {
        closingRoot.classList.remove('tuck-cart-drawer--closing');
        return;
      }

      let timeout;
      const finish = (event) => {
        if (event && (event.target !== panel || event.propertyName !== 'transform')) return;
        panel.removeEventListener('transitionend', finish);
        window.clearTimeout(timeout);
        closingRoot.classList.remove('tuck-cart-drawer--closing');
        if (this.closeAnimationCleanup === cleanup) this.closeAnimationCleanup = null;
      };
      const cleanup = () => finish();
      this.closeAnimationCleanup = cleanup;
      panel.addEventListener('transitionend', finish);
      timeout = window.setTimeout(finish, 320);
    }

    syncScrollLock() {
      const drawerIsOpen = Boolean(this.root && this.root.getAttribute('aria-hidden') === 'false');
      document.documentElement.classList.toggle('tuck-cart-is-open', drawerIsOpen);
    }

    onPageShow() {
      this.syncRoot();
      this.syncScrollLock();
    }

    suppressRebuyCart() {
      requestAnimationFrame(() => {
        const rebuyCart = document.getElementById('rebuy-cart');
        if (!rebuyCart) return;
        rebuyCart.setAttribute('aria-hidden', 'true');
        rebuyCart.setAttribute('inert', '');
        rebuyCart.classList.remove('is-visible');
      });
    }

    isEnabled() {
      return Boolean(this.syncRoot());
    }

    onClick(event) {
      const openTrigger = event.target.closest('.js-drawer-open-cart, [data-tuck-cart-open]');
      if (openTrigger && this.isEnabled()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(openTrigger);
        return;
      }

      if (!this.root || !this.root.contains(event.target)) return;

      if (event.target.closest('[data-tuck-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-tuck-quantity-change]');
      if (quantityButton) {
        const item = quantityButton.closest('[data-tuck-cart-item]');
        const input = item && item.querySelector('[data-tuck-quantity-input]');
        if (!item || !input) return;
        const next = Math.max(0, Number(input.value || 0) + Number(quantityButton.dataset.tuckQuantityChange || 0));
        this.changeLine(item.dataset.lineKey, next, 'quantity-change', item.dataset.variantId, item);
        return;
      }

      const removeButton = event.target.closest('[data-tuck-remove]');
      if (removeButton) {
        const item = removeButton.closest('[data-tuck-cart-item]');
        if (item) this.changeLine(item.dataset.lineKey, 0, 'remove-item', item.dataset.variantId, item);
        return;
      }

      const recommendationAdd = event.target.closest('[data-tuck-rec-add]');
      if (recommendationAdd) {
        this.addRecommendation(recommendationAdd);
        return;
      }

      const previous = event.target.closest('[data-tuck-rec-prev]');
      const next = event.target.closest('[data-tuck-rec-next]');
      if (previous || next) {
        const recommendations = event.target.closest('[data-tuck-recommendations]');
        const track = recommendations && recommendations.querySelector('[data-tuck-rec-track]');
        if (track) {
          const distance = this.recommendationPageDistance(track);
          track.scrollBy({ left: previous ? -distance : distance, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
        }
        return;
      }

      if (event.target.closest('[data-tuck-recommendation-link]')) {
        this.emitAnalytics('cart_recommendation_clicked');
      }

      if (event.target.closest('[data-tuck-checkout]')) {
        const terms = this.root.querySelector('[data-tuck-cart-terms]');
        if (terms && !terms.checked) {
          event.preventDefault();
          this.showError(window.theme?.strings?.cartTermsConfirmation || 'Please agree to the terms before checkout.');
          terms.focus();
          return;
        }
        this.emitAnalytics('checkout_clicked', this.getCartSummary());
      }
    }

    recommendationPageDistance(track) {
      const card = track.querySelector('[data-tuck-recommendation-card]');
      if (!card) return track.clientWidth;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const horizontalPadding =
        (Number.parseFloat(styles.paddingLeft) || 0) + (Number.parseFloat(styles.paddingRight) || 0);
      const step = card.getBoundingClientRect().width + gap;
      const visibleCards = Math.max(1, Math.floor((track.clientWidth - horizontalPadding + gap + 1) / step));

      return step * visibleCards;
    }

    onChange(event) {
      if (!this.root || !this.root.contains(event.target)) return;

      if (event.target.matches('[data-tuck-quantity-input]')) {
        const item = event.target.closest('[data-tuck-cart-item]');
        if (!item) return;
        const quantity = Math.max(0, Number.parseInt(event.target.value, 10) || 0);
        this.changeLine(item.dataset.lineKey, quantity, 'quantity-change', item.dataset.variantId, item);
      }

      if (event.target.matches('[data-tuck-rec-variant]')) {
        const card = event.target.closest('[data-tuck-recommendation-card]');
        const button = card && card.querySelector('[data-tuck-rec-add]');
        if (button) button.disabled = !event.target.value;
        this.updateRecommendationPrice(event.target);
      }

      if (event.target.matches('[data-tuck-cart-note]')) {
        this.updateNote(event.target.value);
      }
    }

    onKeydown(event) {
      if (!this.root || this.root.getAttribute('aria-hidden') !== 'false') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [...this.root.querySelectorAll(SELECTORS.focusable)]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    hasDrawerHistoryState(state = history.state) {
      return Boolean(state && state.__tuckCartDrawer);
    }

    pushDrawerHistoryState() {
      if (!this.supportsDrawerHistory()) return;
      if (this.historyEntryActive || this.hasDrawerHistoryState()) {
        this.historyEntryActive = true;
        return;
      }

      try {
        history.pushState(
          { ...(history.state || {}), __tuckCartDrawer: true },
          '',
          window.location.href
        );
        this.historyEntryActive = true;
      } catch (error) {
        this.historyEntryActive = false;
      }
    }

    onPopstate(event) {
      if (!this.root) return;
      const drawerIsOpen = this.root.getAttribute('aria-hidden') === 'false';
      const isDrawerState = this.hasDrawerHistoryState(event.state);

      if (drawerIsOpen && !isDrawerState) {
        this.historyEntryActive = false;
        this.close({ fromHistory: true });
        return;
      }

      this.historyEntryActive = isDrawerState;
      if (isDrawerState && !drawerIsOpen) this.open();
    }

    open(trigger) {
      if (!this.isEnabled()) return;
      this.cancelCloseAnimation();
      this.resetSwipeGesture();
      this.opener = trigger || document.activeElement;
      const wasOpen = this.root.getAttribute('aria-hidden') === 'false';
      if (!wasOpen) this.pushDrawerHistoryState();
      this.root.removeAttribute('inert');
      this.root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tuck-cart-is-open');

      if (!wasOpen) {
        this.opening = true;
        this.root.classList.remove('tuck-cart-drawer--open');

        const panel = this.root.querySelector(SELECTORS.panel);
        if (!panel) return;

        // Commit the revealed, off-canvas state before starting the transition.
        // Keep focus changes and section replacement out of the animation so
        // mobile browsers can composite one uninterrupted transform.
        panel.getBoundingClientRect();
        let settled = false;
        const finishOpen = () => {
          if (settled) return;
          settled = true;
          this.opening = false;
          panel.removeEventListener('transitionend', onTransitionEnd);
          panel.removeEventListener('transitioncancel', onTransitionCancel);
          if (!this.root || this.root.getAttribute('aria-hidden') !== 'false') return;
          const close = this.root.querySelector('[data-tuck-cart-close]:not(.tuck-cart-drawer__overlay)');
          (close || panel).focus({ preventScroll: true });
          this.loadFallbackRecommendations();
        };
        const onTransitionEnd = (event) => {
          if (event.target === panel && event.propertyName === 'transform') finishOpen();
        };
        const onTransitionCancel = (event) => {
          if (event.target !== panel || event.propertyName !== 'transform') return;
          settled = true;
          this.opening = false;
          panel.removeEventListener('transitionend', onTransitionEnd);
          panel.removeEventListener('transitioncancel', onTransitionCancel);
        };
        panel.addEventListener('transitionend', onTransitionEnd);
        panel.addEventListener('transitioncancel', onTransitionCancel);

        if (this.isIOSWebKit() && !this.reducedMotion()) {
          // iOS Safari can repaint a newly revealed fixed layer more than once
          // while resolving a CSS transition. Drive the same transform through
          // one compositor animation so the panel cannot flash between states.
          this.root.classList.add('tuck-cart-drawer--ios-opening');
          this.openAnimation = panel.animate(
            [
              { transform: 'translate3d(100%, 0, 0)' },
              { transform: 'translate3d(0, 0, 0)' }
            ],
            {
              duration: 220,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
              fill: 'both'
            }
          );
          this.root.classList.add('tuck-cart-drawer--open');
          this.openAnimation.addEventListener('finish', () => {
            this.root?.classList.remove('tuck-cart-drawer--ios-opening');
            this.openAnimation?.cancel();
            this.openAnimation = null;
            finishOpen();
          }, { once: true });
        } else {
          this.root.classList.add('tuck-cart-drawer--open');
        }
        if (this.reducedMotion() || (!this.openAnimation && Number.parseFloat(getComputedStyle(panel).transitionDuration) === 0)) {
          finishOpen();
        }
      } else if (!this.opening) {
        this.loadFallbackRecommendations();
      }

      if (!this.viewTracked) {
        this.viewTracked = true;
        this.emitAnalytics('cart_viewed', this.getCartSummary());
      }
    }

    close({ fromHistory = false, preserveSwipeTransform = false } = {}) {
      if (!this.root) return;
      const wasOpen = this.root.getAttribute('aria-hidden') === 'false';
      if (!wasOpen) return;
      const focusTarget = this.opener && document.contains(this.opener) ? this.opener : null;
      const panel = this.root.querySelector(SELECTORS.panel);
      this.opening = false;
      this.openAnimation?.cancel();
      this.openAnimation = null;
      this.cancelCloseAnimation();
      if (!preserveSwipeTransform) this.resetSwipeGesture();

      // Safari can retain a focus/scroll lock when the focused close button is
      // hidden with its dialog. Move focus out before making the drawer inert.
      if (this.root.contains(document.activeElement)) document.activeElement.blur();
      if (panel) this.startCloseAnimation(panel);
      this.root.classList.remove('tuck-cart-drawer--open', 'tuck-cart-drawer--ios-opening', 'tuck-cart-drawer--dragging');
      this.root.setAttribute('aria-hidden', 'true');
      this.root.setAttribute('inert', '');
      document.documentElement.classList.remove('tuck-cart-is-open');
      requestAnimationFrame(() => {
        if (!focusTarget || !document.contains(focusTarget)) return;
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (error) {
          focusTarget.focus();
        }
      });

      if (
        wasOpen &&
        !fromHistory &&
        this.supportsDrawerHistory() &&
        this.historyEntryActive &&
        this.hasDrawerHistoryState()
      ) {
        this.historyEntryActive = false;
        history.back();
      }
    }

    async changeLine(key, quantity, source, removedVariantId, busyElement) {
      if (!key || this.requestController) return;
      if (busyElement) busyElement.setAttribute('aria-busy', 'true');
      this.clearError();

      try {
        const response = await this.request('/cart/change.js', {
          id: key,
          quantity,
          sections: this.sectionId(),
          sections_url: window.location.pathname
        });
        this.applySectionResponse(response.sections);
        this.dispatchUpdated(response, source, {
          removedVariantId: quantity === 0 ? Number(removedVariantId) : undefined
        });
        this.emitAnalytics(quantity === 0 ? 'cart_item_removed' : 'cart_quantity_changed', {
          variantId: Number(removedVariantId),
          quantity
        });
      } catch (error) {
        this.showError(error.message || 'We could not update your cart. Please try again.');
      } finally {
        if (busyElement && document.contains(busyElement)) busyElement.removeAttribute('aria-busy');
      }
    }

    async addRecommendation(button) {
      if (this.requestController) return;
      const card = button.closest('[data-tuck-recommendation-card]');
      const variant = card && card.querySelector('[data-tuck-rec-variant]');
      const variantId = variant && Number(variant.value);
      const errorElement = card && card.querySelector('[data-tuck-rec-error]');
      if (!variantId) {
        if (errorElement) {
          errorElement.textContent = 'Choose an option first.';
          errorElement.hidden = false;
        }
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (errorElement) errorElement.hidden = true;

      try {
        const response = await this.request('/cart/add.js', {
          items: [{ id: variantId, quantity: 1 }],
          sections: this.sectionId(),
          sections_url: window.location.pathname
        });
        this.applySectionResponse(response.sections);
        this.dispatchUpdated(response, 'recommendation-slider', { addedVariantId: variantId });
        this.emitAnalytics('cart_recommendation_added', { variantId });
      } catch (error) {
        if (errorElement) {
          errorElement.textContent = error.message || 'Could not add this item.';
          errorElement.hidden = false;
        } else {
          this.showError(error.message || 'Could not add this item.');
        }
      } finally {
        if (document.contains(button)) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    async updateNote(note) {
      if (this.requestController) return;
      try {
        const response = await this.request('/cart/update.js', {
          note,
          sections: this.sectionId(),
          sections_url: window.location.pathname
        });
        this.applySectionResponse(response.sections);
        this.dispatchUpdated(response, 'cart-note');
      } catch (error) {
        this.showError(error.message || 'We could not save your note.');
      }
    }

    async request(path, body) {
      if (this.requestController) this.requestController.abort();
      this.requestController = new AbortController();
      const controller = this.requestController;
      try {
        const response = await fetch(`${this.rootPath()}${path.replace(/^\//, '')}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body),
          signal: controller.signal
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Cart request failed.');
        }
        return data;
      } finally {
        if (this.requestController === controller) this.requestController = null;
      }
    }

    async onAjaxAdded(event) {
      if (!this.isEnabled()) return;
      const response = event.detail && event.detail.product;
      if (response && response.sections) {
        this.applySectionResponse(response.sections);
        const variantId = response.variant_id || response.id;
        this.dispatchUpdated(response, 'product-form', { addedVariantId: Number(variantId) });
      } else {
        // When an older product form does not return rendered sections, finish
        // syncing the drawer before revealing it. Replacing the full drawer
        // contents during its transform causes a visible shake in iOS Safari.
        await this.refreshSection('product-form');
      }
      this.open(event.detail && event.detail.addToCartBtn);
    }

    onProductSuccess(event) {
      if (!this.isEnabled()) return;
      const response = event.detail && event.detail.response;
      if (response && response.sections) this.applySectionResponse(response.sections);
      this.open(event.target);
    }

    onProductNavigate(event) {
      if (this.isEnabled()) event.preventDefault();
    }

    async refreshSection(source = 'cart-drawer') {
      const sequence = ++this.sequence;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', this.sectionId());
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        const sections = await response.json();
        if (sequence !== this.sequence) return;
        this.applySectionResponse(sections);
        this.dispatchUpdated({}, source);
      } catch (error) {
        this.showError('We could not refresh your cart. Please try again.');
      }
    }

    applySectionResponse(sections) {
      if (!sections || !this.root) return null;
      const html = sections[this.sectionId()];
      if (!html) return null;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextRender = documentFragment.querySelector(SELECTORS.render);
      const currentRender = this.root.querySelector(SELECTORS.render);
      if (!nextRender || !currentRender) return null;

      const scroll = currentRender.querySelector('.tuck-cart-drawer__scroll');
      const scrollTop = scroll ? scroll.scrollTop : 0;
      const currentTrack = currentRender.querySelector('.tuck-cart-progress__track');
      const nextTrack = nextRender.querySelector('.tuck-cart-progress__track');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let animateProgress = null;

      if (currentTrack && nextTrack && !reduceMotion) {
        const previousProgress = currentTrack.style.getPropertyValue('--tuck-cart-progress');
        const previousSceneLeft = currentTrack.style.getPropertyValue('--tuck-cart-scene-left');
        const targetProgress = nextTrack.style.getPropertyValue('--tuck-cart-progress');
        const targetSceneLeft = nextTrack.style.getPropertyValue('--tuck-cart-scene-left');
        const currentScene = currentTrack.querySelector('.tuck-cart-progress__surf-scene');
        const nextScene = nextTrack.querySelector('.tuck-cart-progress__surf-scene');

        if (previousProgress && targetProgress && previousProgress !== targetProgress) {
          nextTrack.style.setProperty('--tuck-cart-progress', previousProgress);
          if (previousSceneLeft) nextTrack.style.setProperty('--tuck-cart-scene-left', previousSceneLeft);

          let previousScene = null;
          if (currentScene && nextScene) {
            previousScene = currentScene.cloneNode(true);
            previousScene.classList.add('tuck-cart-progress__surf-scene--previous');
            nextScene.style.opacity = '0';
            nextTrack.appendChild(previousScene);
          }

          animateProgress = () => {
            nextTrack.style.setProperty('--tuck-cart-progress', targetProgress);
            if (targetSceneLeft) nextTrack.style.setProperty('--tuck-cart-scene-left', targetSceneLeft);
            if (nextScene) nextScene.style.opacity = '1';
            if (previousScene) {
              previousScene.style.opacity = '0';
              window.setTimeout(() => previousScene.remove(), 360);
            }
          };
        }
      }

      currentRender.replaceWith(nextRender);
      const nextScroll = this.root.querySelector('.tuck-cart-drawer__scroll');
      if (nextScroll) nextScroll.scrollTop = scrollTop;
      if (animateProgress) {
        window.requestAnimationFrame(() => window.requestAnimationFrame(animateProgress));
      }
      this.updateCartBubble();
      this.loadFallbackRecommendations();
      this.rankRecommendationsByCartSize();
      return this.getCartSummary();
    }

    getCartSummary() {
      const render = this.root && this.root.querySelector(SELECTORS.render);
      if (!render) return null;
      return {
        item_count: Number(render.dataset.cartCount || 0),
        total_price: Number(render.dataset.cartSubtotal || 0),
        items_subtotal_price: Number(render.dataset.cartItemsSubtotal || 0),
        product_ids: (render.dataset.cartProductIds || '')
          .split('|')
          .map(Number)
          .filter(Boolean)
      };
    }

    dispatchUpdated(response, source, extra = {}) {
      const cart = this.getCartSummary();
      const detail = { cart, source, response, ...extra };
      document.dispatchEvent(new CustomEvent('tuckchar:cart-updated', { detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      this.updateCartBubble();
    }

    updateCartBubble() {
      const count = this.getCartSummary()?.item_count || 0;
      document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
        const countElement = bubble.querySelector('.cart-link__bubble-num');
        if (countElement) countElement.textContent = count;
        bubble.classList.toggle('cart-link__bubble--visible', count > 0);
        const cartLink = bubble.closest('.js-drawer-open-cart');
        if (cartLink) {
          const label = cartLink.dataset.cartLabel || 'Cart';
          cartLink.setAttribute('aria-label', `${label}: ${count}`);
        }
      });
    }

    updateRecommendationPrice(select) {
      if (!select.value) return;
      const option = select.options[select.selectedIndex];
      const card = select.closest('[data-tuck-recommendation-card]');
      const price = card && card.querySelector('[data-tuck-rec-price]');
      const compare = card && card.querySelector('[data-tuck-rec-compare]');
      if (price) price.innerHTML = this.formatMoney(Number(option.dataset.price || 0));
      if (compare) {
        const compareValue = Number(option.dataset.compare || 0);
        compare.textContent = compareValue ? this.formatMoney(compareValue) : '';
        compare.hidden = !compareValue;
      }
    }

    async loadFallbackRecommendations() {
      const render = this.root && this.root.querySelector(SELECTORS.render);
      const container = render && render.querySelector('[data-tuck-recommendations]');
      if (!container || container.dataset.fallbackLoading === 'true') return;
      const productId = Number(render.dataset.primaryProductId || 0);
      if (!productId) return;

      const track = container.querySelector('[data-tuck-rec-track]');
      if (!track) return;
      const limit = Number(container.dataset.limit || 6);
      const visibleCount = this.rankRecommendationsByCartSize();
      const isCoastalCart = render.dataset.coastalCart === 'true';
      if (isCoastalCart && visibleCount > 0) return;
      if (visibleCount >= limit || container.dataset.fallbackLoaded === 'true') return;

      const cacheKey = `${productId}:${container.dataset.limit || 6}`;
      let products = this.recommendationCache.get(cacheKey);
      if (!products) {
        if (this.recommendationController) this.recommendationController.abort();
        this.recommendationController = new AbortController();
        const sequence = ++this.sequence;
        container.dataset.fallbackLoading = 'true';
        try {
          const recommendationLimit = Math.min(10, Math.max(limit * 2, 6));
          const recommendationUrls = ['complementary', 'related'].map((intent) =>
            `${this.rootPath()}recommendations/products.json?product_id=${productId}&limit=${recommendationLimit}&intent=${intent}`
          );
          const recommendationResponses = await Promise.all(recommendationUrls.map(async (url) => {
            const response = await fetch(url, { signal: this.recommendationController.signal });
            return response.ok ? response.json() : { products: [] };
          }));
          const bestSellerUrl = `${this.rootPath()}collections/all/products.json?limit=50&sort_by=best-selling`;
          const bestSellerResponse = await fetch(bestSellerUrl, { signal: this.recommendationController.signal });
          const bestSellerData = bestSellerResponse.ok ? await bestSellerResponse.json() : { products: [] };
          if (sequence !== this.sequence) return;
          const seen = new Set();
          products = [
            ...(bestSellerData.products || []),
            ...recommendationResponses.flatMap((data) => data.products || []),
          ].filter((product) => {
            const id = Number(product.id);
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          this.recommendationCache.set(cacheKey, products);
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('[Tuck cart] Recommendations unavailable.', error);
          return;
        } finally {
          delete container.dataset.fallbackLoading;
        }
      }

      const cartProductIds = new Set(this.getCartSummary()?.product_ids || []);
      const existingProductIds = new Set(
        [...track.querySelectorAll('[data-tuck-recommendation-card]')]
          .map((card) => Number(card.dataset.productId))
          .filter(Boolean)
      );
      const cartSizes = this.cartSizeTokens();
      const eligible = products.filter((product) =>
        product.available && !cartProductIds.has(Number(product.id)) &&
        !existingProductIds.has(Number(product.id)) &&
        Array.isArray(product.variants) && product.variants.some((variant) => variant.available) &&
        (isCoastalCart || this.productMatchesCartSizes(product, cartSizes))
      ).slice(0, limit);
      eligible.forEach((product) => track.append(this.buildRecommendationCard(product)));
      container.dataset.fallbackLoaded = 'true';
      container.dataset.renderedCount = track.querySelectorAll('[data-tuck-recommendation-card]').length;
      const finalVisibleCount = this.rankRecommendationsByCartSize();
      container.hidden = finalVisibleCount === 0;
      if (eligible.length) {
        this.emitAnalytics('cart_recommendation_viewed', { productIds: eligible.map((product) => product.id) });
      }
    }

    buildRecommendationCard(product) {
      const article = document.createElement('article');
      article.className = 'tuck-cart-rec';
      article.dataset.tuckRecommendationCard = '';
      article.dataset.productId = product.id;
      article.dataset.productTitle = product.title || '';
      article.dataset.productHandle = product.handle || '';

      const link = document.createElement('a');
      link.className = 'tuck-cart-rec__image';
      link.href = product.url;
      link.dataset.tuckRecommendationLink = '';
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = this.imageUrl(product.featured_image, 240);
        image.alt = product.title;
        image.width = 240;
        image.height = 240;
        image.loading = 'lazy';
        link.append(image);
      }

      const body = document.createElement('div');
      body.className = 'tuck-cart-rec__body';
      const title = document.createElement('a');
      title.className = 'tuck-cart-rec__title';
      title.href = product.url;
      title.dataset.tuckRecommendationLink = '';
      title.textContent = product.title;

      const variants = product.variants.filter((variant) => variant.available);
      const sizeOptionIndex = (product.options || []).findIndex((option) =>
        /size/i.test(typeof option === 'string' ? option : option.name || '')
      );
      const availableSizes = variants
        .map((variant) => sizeOptionIndex >= 0 ? variant.options?.[sizeOptionIndex] : '')
        .filter(Boolean);
      article.dataset.availableSizes = availableSizes.map((size) => `|${size}|`).join('');
      const price = document.createElement('div');
      price.className = 'tuck-cart-rec__price';
      const priceValue = document.createElement('span');
      priceValue.dataset.tuckRecPrice = '';
      priceValue.textContent = this.formatMoney(variants[0].price);
      price.append(priceValue);

      let variantControl;
      if (variants.length === 1) {
        variantControl = document.createElement('input');
        variantControl.type = 'hidden';
        variantControl.value = variants[0].id;
      } else {
        variantControl = document.createElement('select');
        variantControl.className = 'tuck-cart-rec__select';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Choose an option';
        variantControl.append(placeholder);
        variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title;
          option.dataset.price = variant.price;
          option.dataset.compare = variant.compare_at_price || 0;
          if (sizeOptionIndex >= 0 && variant.options?.[sizeOptionIndex]) {
            option.dataset.size = variant.options[sizeOptionIndex];
          }
          variantControl.append(option);
        });
      }
      variantControl.dataset.tuckRecVariant = '';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tuck-cart-rec__add';
      button.dataset.tuckRecAdd = '';
      button.textContent = 'Add';
      button.disabled = variants.length > 1;

      const error = document.createElement('p');
      error.className = 'tuck-cart-rec__error';
      error.dataset.tuckRecError = '';
      error.setAttribute('role', 'alert');
      error.hidden = true;

      body.append(title, price, variantControl, button, error);
      article.append(link, body);
      return article;
    }

    rankRecommendationsByCartSize() {
      const track = this.root?.querySelector('[data-tuck-rec-track]');
      if (!track) return 0;
      const render = this.root?.querySelector(SELECTORS.render);
      if (render?.dataset.coastalCart === 'true') {
        return this.rankCoastalRecommendations(track);
      }
      const cartSizes = this.cartSizeTokens();
      const limit = Number(track.closest('[data-tuck-recommendations]')?.dataset.limit || 6);

      const cards = [...track.querySelectorAll('[data-tuck-recommendation-card]')];
      const ranked = cards
        .map((card, index) => {
          const sizes = String(card.dataset.availableSizes || '')
            .split('|')
            .map((size) => this.normalizeSizeToken(size))
            .filter(Boolean);
          const exact = !cartSizes.size || sizes.some((size) => cartSizes.has(size));
          const flexible = sizes.some((size) => ['OS', 'O/S', 'ONESIZE', 'ADJUSTABLE'].includes(size));
          return { card, index, eligible: exact || flexible || !sizes.length, rank: exact ? 0 : flexible || !sizes.length ? 1 : 2 };
        })
        .sort((left, right) => left.rank - right.rank || left.index - right.index);

      let visibleCount = 0;
      ranked.forEach(({ card, eligible }) => {
        const visible = eligible && visibleCount < limit;
        card.hidden = !visible;
        if (visible) {
          visibleCount += 1;
          this.selectMatchingRecommendationVariant(card, cartSizes);
        }
        track.append(card);
      });
      const container = track.closest('[data-tuck-recommendations]');
      if (container) container.hidden = visibleCount === 0;
      return visibleCount;
    }

    rankCoastalRecommendations(track) {
      const limit = Number(track.closest('[data-tuck-recommendations]')?.dataset.limit || 24);
      const sourceProducts = [...this.root.querySelectorAll('[data-tuck-cart-item]')].map((item) => ({
        family: this.pajamaFamily(item.dataset.productTitle || item.dataset.productHandle),
        role: this.pajamaRole(item.dataset.productTitle || item.dataset.productHandle)
      }));
      const cards = [...track.querySelectorAll('[data-tuck-recommendation-card]')];
      const pairedFamilies = {
        GREENPOSTCARDS: 'PINKPOSTCARDS',
        PINKPOSTCARDS: 'GREENPOSTCARDS',
        BLUEBEACHHUTS: 'PINKBEACHHUTS',
        PINKBEACHHUTS: 'BLUEBEACHHUTS'
      };

      const rolePriority = {
        adult: { dog: 0, kid: 2, baby: 3, adult: 4, other: 8 },
        kid: { adult: 0, dog: 1, baby: 2, kid: 3, other: 8 },
        baby: { adult: 0, dog: 1, kid: 2, baby: 3, other: 8 },
        dog: { adult: 0, kid: 1, baby: 2, dog: 3, other: 8 },
        other: { adult: 0, dog: 1, kid: 2, baby: 3, other: 8 }
      };

      const ranked = cards.map((card, index) => {
        const candidateFamily = this.pajamaFamily(card.dataset.productTitle || card.dataset.productHandle);
        const candidateRole = this.pajamaRole(card.dataset.productTitle || card.dataset.productHandle);
        const scores = sourceProducts.map((source) => {
          const sameFamily = source.family && source.family === candidateFamily;
          const pairedFamily = source.family && pairedFamilies[source.family] === candidateFamily;
          const familyScore = sameFamily ? 0 : pairedFamily ? 10 : 40;
          const roleScore = rolePriority[source.role]?.[candidateRole] ?? 8;
          const pairedAdultBoost = pairedFamily && source.role === 'adult' && candidateRole === 'adult' ? -8 : 0;
          return familyScore + roleScore + pairedAdultBoost;
        });
        return { card, index, score: scores.length ? Math.min(...scores) : index };
      }).sort((left, right) => left.score - right.score || left.index - right.index);

      ranked.forEach(({ card }, index) => {
        card.hidden = index >= limit;
        track.append(card);
      });
      const visibleCount = Math.min(ranked.length, limit);
      const container = track.closest('[data-tuck-recommendations]');
      if (container) container.hidden = visibleCount === 0;
      return visibleCount;
    }

    pajamaFamily(value) {
      const token = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (token.includes('MERRYREEF')) return 'MERRYREEF';
      if (token.includes('JOLLYWAVES')) return 'JOLLYWAVES';
      if (token.includes('GREENCOASTALPOSTCARD')) return 'GREENPOSTCARDS';
      if (token.includes('PINKCOASTALPOSTCARD')) return 'PINKPOSTCARDS';
      if (token.includes('BLUEBEACHHUT')) return 'BLUEBEACHHUTS';
      if (token.includes('PINKBEACHHUT')) return 'PINKBEACHHUTS';
      return '';
    }

    pajamaRole(value) {
      const token = String(value || '').toLowerCase();
      if (/(dog|bandana)/.test(token)) return 'dog';
      if (/(baby|zippy|shortie|romper|bow)/.test(token)) return 'baby';
      if (/(kid|kids|girl|girls|boy|boys|toddler|youth)/.test(token)) return 'kid';
      if (/(adult|women|woman|men|man|unisex)/.test(token)) return 'adult';
      return 'other';
    }

    cartSizeTokens() {
      return new Set(
        [...(this.root?.querySelectorAll('[data-tuck-cart-item][data-cart-size]') || [])]
          .map((item) => this.normalizeSizeToken(item.dataset.cartSize))
          .filter(Boolean)
      );
    }

    productMatchesCartSizes(product, cartSizes) {
      if (!cartSizes.size) return true;
      const sizeOptionIndex = (product.options || []).findIndex((option) =>
        String(option.name || option).toLowerCase().includes('size')
      );
      if (sizeOptionIndex < 0) return true;
      return (product.variants || []).some((variant) => {
        if (!variant.available) return false;
        const size = this.normalizeSizeToken(variant.options?.[sizeOptionIndex]);
        return cartSizes.has(size) || ['OS', 'O/S', 'ONESIZE', 'ADJUSTABLE'].includes(size);
      });
    }

    selectMatchingRecommendationVariant(card, cartSizes) {
      const select = card.querySelector('select[data-tuck-rec-variant]');
      if (!select || !cartSizes.size) return;
      const option = [...select.options].find((candidate) =>
        cartSizes.has(this.normalizeSizeToken(candidate.dataset.size))
      );
      if (!option) return;
      select.value = option.value;
      const button = card.querySelector('[data-tuck-rec-add]');
      if (button) button.disabled = false;
      this.updateRecommendationPrice(select);
    }

    normalizeSizeToken(value) {
      return String(value || '')
        .trim()
        .toUpperCase()
        .replace(/\bYEARS?\b/g, 'Y')
        .replace(/[^A-Z0-9]/g, '');
    }

    imageUrl(source, width) {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    }

    sectionId() {
      return this.root?.dataset.sectionId || 'tuck-cart-drawer';
    }

    rootPath() {
      const path = this.root?.dataset.cartRoot || window.Shopify?.routes?.root || '/';
      return path.endsWith('/') ? path : `${path}/`;
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }

    clearError() {
      const error = this.root && this.root.querySelector(SELECTORS.error);
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    showError(message) {
      const error = this.root && this.root.querySelector(SELECTORS.error);
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    emitAnalytics(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`tuckchar:${name}`, { detail }));
      document.dispatchEvent(new CustomEvent('tuckchar:analytics', {
        detail: { event: name, ...detail }
      }));
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    isIOSWebKit() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    supportsDrawerHistory() {
      return /Android/i.test(navigator.userAgent);
    }
  }

  const start = () => {
    window.tuckCartController = new TuckCartController();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
